import { useCallback, useState } from "react";

interface UseFileDropzoneOptions {
  /** Accepted extensions, lower-case with the leading dot, e.g. [".xlsx", ".csv"]. */
  accept: string[];
  /** Called with the first dropped file whose extension is accepted. */
  onFile: (file: File) => void;
  /** Called with a dropped file whose extension is NOT accepted (optional). */
  onReject?: (file: File) => void;
}

/**
 * Drag-and-drop support for a file dropzone. Returns a `dragActive` flag (for
 * hover styling) and `dropzoneProps` to spread onto the drop target.
 *
 * The native `accept` attribute only filters the click-to-open picker, not
 * drops, so this validates the dropped file's extension itself and routes
 * unaccepted files to `onReject`.
 */
export const useFileDropzone = ({
  accept,
  onFile,
  onReject,
}: UseFileDropzoneOptions) => {
  const [dragActive, setDragActive] = useState(false);

  const isAccepted = useCallback(
    (file: File) =>
      accept.some((ext) => file.name.toLowerCase().endsWith(ext.toLowerCase())),
    [accept],
  );

  // `dragOver` fires continuously while hovering, so asserting `true` here keeps
  // the highlight steady even as `dragLeave` flickers over child elements.
  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!dragActive) setDragActive(true);
    },
    [dragActive],
  );

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      const dropped = e.dataTransfer.files?.[0];
      if (!dropped) return;
      if (isAccepted(dropped)) {
        onFile(dropped);
      } else {
        onReject?.(dropped);
      }
    },
    [isAccepted, onFile, onReject],
  );

  return {
    dragActive,
    dropzoneProps: { onDragOver, onDragLeave, onDrop },
  };
};
